import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { baseUrl, postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import React, { useState } from "react";
import { IconButton, Tooltip } from "@kaltura/ds-react-components";
import { Clear24Icon } from "@kaltura/ds-react-icons";
import { KalturaESearchEntryResult } from "kaltura-typescript-client/api/types/KalturaESearchEntryResult";
import { ActionResponse } from "../types";
import { AccumulatedResults } from "@kaltura/mediaspace-shared-search";

export interface RemoveProps {
    /**
     * Function to update the cached results after removal
     * Receives an updater function that transforms the old data
     */
    updateCachedResults?: (
        updater: (
            oldData: AccumulatedResults<KalturaESearchEntryResult>
        ) => AccumulatedResults<KalturaESearchEntryResult>
    ) => void;

    /**
     * selected entry id
     */
    entryId: string;

    /**
     * name of current entry
     */
    entryName: string;
}

/**
 * my-history remove single entry
 */
export const Remove = (props: RemoveProps) => {
    const { updateCachedResults, entryId, entryName } = props;
    const [loading, setLoading] = useState(false);
    const { showToast } = useToastsContext();

    /**
     * remove entry handler
     */
    const removeEntryFromList = async () => {
        setLoading(true);
        try {
            const result: ActionResponse = await postKmsData(
                `${baseUrl}/history-ds/remove`,
                {
                    entryId,
                }
            );
            if (result.success) {
                const messageText = translate(`"%1" has been removed from your history.`, [entryName]);
                showToast({ severity: "success", message: messageText });

                /**
                 * Update cached results by filtering out the removed entry.
                 *
                 * UX Rationale: My History uses time-based pagination (not page-based).
                 * After the server successfully removes an entry, we update the local cache
                 * to reflect the removal without requiring a full page reload. This provides
                 * immediate visual feedback and maintains the user's scroll position and context.
                 *
                 * Since pagination is based on timestamps (using 'until' parameter), removing
                 * an entry from the cache doesn't affect the pagination logic - the next
                 * "Load More" will still fetch entries older than the last visible entry.
                 * This ensures a seamless experience where removed entries disappear once the
                 * removal is confirmed, and subsequent loads continue to work correctly.
                 */
                updateCachedResults?.((oldData) => {
                    if (!oldData?.entryResults) {
                        return oldData;
                    }

                    return {
                        ...oldData,
                        entryResults: {
                            objects: oldData.entryResults.objects.filter((obj) => obj.object.id !== entryId),
                            totalCount: Math.max(0, oldData.entryResults.totalCount - 1),
                        },
                    };
                });
            }
            else {
                console.error("Remove entry error:", result);
                showToast({
                    severity: "error",
                    message: translate(`Failed to remove "%1" from your history.`, [entryName]),
                });
            }
        }
        catch (error) {
            console.error("Remove entry error:", error);
            showToast({
                severity: "error",
                message: translate(`Failed to remove "%1" from your history.`, [entryName]),
            });
        }

        setLoading(false);
    };

    return (
        <Tooltip title={translate("Remove from My History")}>
            <IconButton
                variant={"borderless"}
                color={"secondary"}
                loading={loading}
                aria-label={translate("Remove %1 from My History", [entryName])}
                onClick={removeEntryFromList}
            >
                <Clear24Icon />
            </IconButton>
        </Tooltip>
    );
};

export default Remove;
